import Color from '@/containers/ColorPicker';
import Presentation from '@/components/Properties/Groups/TextProperties/Presentation';
import TextCase from '@/components/Properties/Groups/TextProperties/TextCase';

import { useAppCommon, useElementProperties } from '@/hooks';
import { EVENT_TYPE } from '@/common/constants/eventType';
import {
  DEFAULT_TEXT,
  PRESENTATION,
  PROPERTIES_SELECTION_FABRICS,
  TWO_EM_DASH
} from '@/common/constants';
import {
  convertObjectStyleToArray,
  getCurrentCanvas,
  getUniqueStylesWithPropertyName
} from '@/common/utils';

export default {
  components: {
    Color,
    Presentation,
    TextCase
  },
  setup() {
    const { getProperty } = useElementProperties();
    const { currentSelectionTextStyle } = useAppCommon();

    return {
      getProperty,
      currentSelectionTextStyle
    };
  },
  data() {
    return {
      componentKey: true
    };
  },
  computed: {
    defaultColor() {
      return this.getProperty('color') || DEFAULT_TEXT.COLOR;
    },
    color() {
      this.componentKey = !!this.componentKey;
      return this.updateColor();
    },
    selectedCase() {
      return '';
    },
    selectedStyles() {
      return this.updateTextPresentation();
    }
  },
  methods: {
    getDefaultPresentationValue() {
      return {
        isBold: this.selectedStyles.indexOf(PRESENTATION.BOLD) >= 0,
        isItalic: this.selectedStyles.indexOf(PRESENTATION.ITALIC) >= 0,
        isUnderline: this.selectedStyles.indexOf(PRESENTATION.UNDERLINE) >= 0
      };
    },
    applyValuesChange(oldValue, value) {
      const valuesChange = {
        isSelectionStyle: true
      };
      for (const key in oldValue) {
        if (Object.hasOwnProperty.call(oldValue, key)) {
          const element = oldValue[key];
          if (element !== value[key]) {
            valuesChange[key] = value[key];
          }
        }
      }
      return valuesChange;
    },
    /**
     * Change Presentation/TextCase of text box selected
     * @param  {String} value Receive value information to change
     */
    onChangePresentation(value) {
      value.isSelectionStyle = true;
      const oldValue = this.getDefaultPresentationValue();
      this.$root.$emit(
        EVENT_TYPE.CHANGE_TEXT_PROPERTIES,
        this.applyValuesChange(oldValue, value)
      );
    },

    onChangeTextCase(value) {
      value.isSelectionStyle = true;
      this.$root.$emit(EVENT_TYPE.CHANGE_TEXT_PROPERTIES, value);
    },
    /**
     * Change Color of text box selected
     * @param  {String} color Receive value information to change
     */
    onChangeColor(color) {
      this.forceRenderComponent();
      this.$root.$emit(EVENT_TYPE.CHANGE_TEXT_PROPERTIES, {
        color,
        isSelectionStyle: true
      });
    },
    updateColor() {
      const listStyle = getUniqueStylesWithPropertyName(
        PROPERTIES_SELECTION_FABRICS.COLOR,
        this.currentSelectionTextStyle
      );
      if (!listStyle.length) {
        return this.getProperty('color');
      }
      const canvas = window.printCanvas || window.digitalCanvas;
      if (listStyle.length === 1) {
        if (
          canvas.getActiveObject().type === 'group' &&
          this.getProperty('color') !==
            listStyle[0][PROPERTIES_SELECTION_FABRICS.COLOR]
        ) {
          return TWO_EM_DASH;
        }

        return listStyle[0][PROPERTIES_SELECTION_FABRICS.COLOR];
      } else {
        return TWO_EM_DASH;
      }
    },
    forceRenderComponent() {
      this.componentKey = !this.componentKey;
    },
    getCurrentSelectTextPresentation(isBold, isItalic, isUnderline) {
      return [
        isBold ? PRESENTATION.BOLD : '',
        isItalic ? PRESENTATION.ITALIC : '',
        isUnderline ? PRESENTATION.UNDERLINE : ''
      ];
    },
    checkPresentationSelectionAllText(
      presentationStyle,
      listStylePresentation,
      listGroupStyles
    ) {
      const presentationProperties = {
        [PROPERTIES_SELECTION_FABRICS.IS_BOLD]: 'isBold',
        [PROPERTIES_SELECTION_FABRICS.IS_ITALIC]: 'isItalic',
        [PROPERTIES_SELECTION_FABRICS.IS_UNDERLINE]: 'isUnderline'
      };
      if (listStylePresentation.length === this.getProperty('text')?.length) {
        return true;
      } else {
        const listStylesNoPresentation = listGroupStyles.filter(
          item => item[presentationStyle] === ''
        );
        if (
          this.getProperty(presentationProperties[presentationStyle]) &&
          !listStylesNoPresentation.length
        ) {
          return true;
        }
      }
      return false;
    },
    updateTextPresentation() {
      const canvas = getCurrentCanvas();
      const currentObject = canvas.getActiveObject();
      let currentSelectValue = [];

      const {
        listStyleBold,
        listStyleItalic,
        listStyleUnderline
      } = this.getCurrentPresentationData();

      if (currentObject?.type === 'group') {
        const listGroupStyles = convertObjectStyleToArray(
          this.getProperty('selectionStyles')
        );
        const listStyleBoldGroup = listGroupStyles.filter(
          item => item[PROPERTIES_SELECTION_FABRICS.IS_BOLD]
        );
        const listStyleItalicGroup = listGroupStyles.filter(
          item => item[PROPERTIES_SELECTION_FABRICS.IS_ITALIC]
        );
        const listStyleUnderlineGroup = listGroupStyles.filter(
          item => item[PROPERTIES_SELECTION_FABRICS.IS_UNDERLINE]
        );
        let isBold = false;
        let isItalic = false;
        let isUnderline = false;
        /* set value for bold property */
        isBold = this.checkPresentationSelectionAllText(
          PROPERTIES_SELECTION_FABRICS.IS_BOLD,
          listStyleBoldGroup,
          listGroupStyles
        );
        /* set value for bold property */

        /* set value for italic property */
        isItalic = this.checkPresentationSelectionAllText(
          PROPERTIES_SELECTION_FABRICS.IS_ITALIC,
          listStyleItalicGroup,
          listGroupStyles
        );
        /* set value for italic property */

        /* set value for underline property */
        isUnderline = this.checkPresentationSelectionAllText(
          PROPERTIES_SELECTION_FABRICS.IS_UNDERLINE,
          listStyleUnderlineGroup,
          listGroupStyles
        );
        /* set value for underline property */
        currentSelectValue = this.getCurrentSelectTextPresentation(
          isBold,
          isItalic,
          isUnderline
        );
        return currentSelectValue;
      }
      // case for editting mode

      /* apply bold property */
      let isBoldSelectionText = false;
      const listSelectionStylesNoBold = this.currentSelectionTextStyle.filter(
        item => item[PROPERTIES_SELECTION_FABRICS.IS_BOLD] === ''
      );
      if (this.getProperty('isBold')) {
        if (!listSelectionStylesNoBold.length) {
          isBoldSelectionText = true;
        }
      } else {
        isBoldSelectionText = listStyleBold.length === 1;
      }
      /* apply bold property */

      /* apply italic property */
      let isItalicSelectionText = false;
      const listSelectionStylesNoItalic = this.currentSelectionTextStyle.filter(
        item => item[PROPERTIES_SELECTION_FABRICS.IS_ITALIC] === ''
      );
      if (this.getProperty('isItalic')) {
        if (!listSelectionStylesNoItalic.length) {
          isItalicSelectionText = true;
        }
      } else {
        isItalicSelectionText = listStyleItalic.length === 1;
      }
      /* apply italic property */

      /* apply underline property */
      let isUnderlineSelectionText = false;
      const listSelectionStylesNoUnderline = this.currentSelectionTextStyle.filter(
        item => item[PROPERTIES_SELECTION_FABRICS.IS_UNDERLINE] === false
      );
      if (this.getProperty('isUnderline')) {
        if (!listSelectionStylesNoUnderline.length) {
          isUnderlineSelectionText = true;
        }
      } else {
        isUnderlineSelectionText = listStyleUnderline.length === 1;
      }
      /* apply underline property */
      currentSelectValue = this.getCurrentSelectTextPresentation(
        isBoldSelectionText,
        isItalicSelectionText,
        isUnderlineSelectionText
      );
      return currentSelectValue;
    },
    getCurrentPresentationData() {
      const listStyleBold = getUniqueStylesWithPropertyName(
        PROPERTIES_SELECTION_FABRICS.IS_BOLD,
        this.currentSelectionTextStyle
      );
      const listStyleItalic = getUniqueStylesWithPropertyName(
        PROPERTIES_SELECTION_FABRICS.IS_ITALIC,
        this.currentSelectionTextStyle
      );
      const listStyleUnderline = getUniqueStylesWithPropertyName(
        PROPERTIES_SELECTION_FABRICS.IS_UNDERLINE,
        this.currentSelectionTextStyle
      );
      return {
        listStyleBold,
        listStyleItalic,
        listStyleUnderline
      };
    },
    getPresentaionOfGroup() {
      const isBold = this.getProperty('isBold') || false;

      const isItalic = this.getProperty('isItalic') || false;

      const isUnderline = this.getProperty('isUnderline') || false;
      return {
        isBold,
        isItalic,
        isUnderline
      };
    }
  }
};
